import { useEffect } from "react";
import { X, Copy, AlertTriangle, Phone, ExternalLink, Reply } from "lucide-react";
import TemplatePreview from "./TemplatePreview";
import { STATUS_BADGES, CATEGORY_LABEL, HEADER_FORMAT_LABEL, findComponent } from "./templateMeta";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

/** Destaca {{1}}, {{2}}... no texto bruto do template. */
function HighlightVars({ text }) {
  if (!text) return <span className="text-ink-5">(vazio)</span>;
  const parts = text.split(/(\{\{\d+\}\})/g);
  return (
    <>
      {parts.map((p, i) =>
        /^\{\{\d+\}\}$/.test(p) ? (
          <span key={i} className="text-brand-300 bg-brand-500/10 rounded px-1 font-mono text-[0.8em]">{p}</span>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-ink-5 mb-0.5">{label}</div>
      <div className="text-sm text-ink-2">{children}</div>
    </div>
  );
}

export default function TemplateViewModal({ row, onClose, onDuplicate }) {
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!row) return null;

  const badge = STATUS_BADGES[row.status] || STATUS_BADGES.DISABLED;
  const header = findComponent(row.components, "HEADER");
  const body = findComponent(row.components, "BODY");
  const footer = findComponent(row.components, "FOOTER");
  const buttons = findComponent(row.components, "BUTTONS")?.buttons || [];
  const bodyExamples = body?.example?.body_text?.[0];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-surface-1 border border-white/[0.06] rounded-lg w-full max-w-4xl my-8 max-h-[calc(100vh-4rem)] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-6 py-4">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-white font-mono truncate">{row.name}</h3>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`text-xs font-medium border rounded px-2 py-0.5 ${badge.cls}`}>{badge.label}</span>
              <span className="text-xs text-ink-4">{row.language}</span>
              <span className="text-xs text-ink-4">·</span>
              <span className="text-xs text-ink-4">{CATEGORY_LABEL[row.category] || row.category}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-ink-4 hover:text-white flex-shrink-0" aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 overflow-y-auto">
          <div className="space-y-5">
            {row.status === "REJECTED" && row.rejection_reason && (
              <div className="bg-red-950 border border-red-900 text-red-300 text-xs rounded p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-medium mb-0.5">Motivo da rejeição</div>
                  <div className="text-red-200/80">{row.rejection_reason}</div>
                </div>
              </div>
            )}

            {header && (
              <Field label={`Header · ${HEADER_FORMAT_LABEL[header.format] || header.format || "—"}`}>
                {header.format === "TEXT"
                  ? <div className="whitespace-pre-wrap break-words"><HighlightVars text={header.text} /></div>
                  : <span className="text-ink-4">Mídia enviada no momento do disparo</span>}
              </Field>
            )}

            <Field label="Mensagem (body)">
              <div className="bg-base border border-white/[0.06] rounded p-3 whitespace-pre-wrap break-words leading-relaxed">
                <HighlightVars text={body?.text} />
              </div>
              {Array.isArray(bodyExamples) && bodyExamples.length > 0 && (
                <div className="text-xs text-ink-4 mt-1.5">
                  Exemplos: {bodyExamples.map((ex, i) => (
                    <span key={i} className="mr-2"><span className="font-mono text-ink-5">{`{{${i + 1}}}`}</span> = {String(ex)}</span>
                  ))}
                </div>
              )}
            </Field>

            {footer?.text && (
              <Field label="Footer"><div className="whitespace-pre-wrap break-words">{footer.text}</div></Field>
            )}

            {buttons.length > 0 && (
              <Field label={`Botões (${buttons.length})`}>
                <ul className="space-y-1">
                  {buttons.map((b, i) => {
                    let Icon = Reply;
                    if (b.type === "URL") Icon = ExternalLink;
                    if (b.type === "PHONE_NUMBER") Icon = Phone;
                    const extra = b.url || b.phone_number || null;
                    return (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <Icon className="w-3.5 h-3.5 text-ink-4 flex-shrink-0" />
                        <span className="text-ink-2">{b.text || "(sem texto)"}</span>
                        {extra && <span className="text-xs text-ink-5 truncate">{extra}</span>}
                      </li>
                    );
                  })}
                </ul>
              </Field>
            )}

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-white/[0.06]">
              <Field label="ID na Meta"><span className="font-mono text-xs break-all">{row.meta_template_id || "—"}</span></Field>
              <Field label="Qualidade">{row.quality_score || "—"}</Field>
              <Field label="Última sincronização">{formatDate(row.data_atualizacao)}</Field>
              <Field label="ID interno">#{row.id}</Field>
            </div>
          </div>

          <div>
            <TemplatePreview components={row.components} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/[0.06]">
          <button type="button" onClick={onClose} className="text-sm text-ink-2 hover:text-white px-4 py-2">
            Fechar
          </button>
          {onDuplicate && (
            <button
              type="button"
              onClick={() => onDuplicate(row)}
              className="bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded text-sm font-medium inline-flex items-center gap-2"
            >
              <Copy className="w-4 h-4" /> Duplicar como novo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
